// Shared types and utilities for processed-entry components.

import type { PipelineStepInput } from '../../PipelineStepsWidget'
import { parseDbTimestamp } from '@/lib/dates'

// ─── Theme ────────────────────────────────────────────────────────────────────

export type ProcessedTheme = 'dark' | 'light'

// ─── Data types ───────────────────────────────────────────────────────────────

export type ProcessedEntry = {
  id: string
  createdAt: string
  headline: string | null
  title: string | null
  messageContent: string | null
  sender: string | null
  processedInputDescription: string | null
  postType: string | null
  platformNameCode: string | null
  platformName: string | null
  isScam: boolean
  userEntryId?: string | null
  userEntryDomainId?: string | null
  articleUrl?: string | null
  originalUrl?: string | null
  lastMobileIsAlive?: boolean | null
  lastDesktopIsAlive?: boolean | null
  lastBrowserDataAt?: string | null
  browserDataCount?: number
  manualInactive?: boolean
  lastDomainIsActive?: boolean | null
  lastProbeIsAlive?: boolean | null
  rawEntryR2Key?: string | null
  // Present on the raw processed_entries_flat-backed payload (camelCased) but
  // not previously declared here.
  messageTypeId?: number | string | null
  messageTypeName?: string | null
  // processed_entries_flat's own source_type column (e.g. "ctt_trustfence_email"
  // for CTT's inbound mail-alert channel) — already selected by the backend's
  // getEntriesByPlatformIds query and camelCased through, just not previously
  // typed here. Same field SIBS's own entries table shows as "Source_Type".
  sourceType?: string | null
  pipelineSteps?: PipelineStepInput[]
  pipeline_steps?: PipelineStepInput[]
}

export type DomainDetail = {
  id: string
  originalUrl: string
  isSubdomain: boolean
  manualInactive: boolean
  domainId: string
  createdAt: string
  domainInfo: Record<string, any> | null
  domainMetadata: Record<string, any> | null
}

// A tagged description derived from a browser-data row (e.g. an LLM-generated
// description of what a screenshot shows). A given entry_browser_data row may
// have zero, one, or several of these (one per tag) — never assume presence.
export type BrowserDataDescription = {
  id: string
  tag: string
  description: string | null
  createdAt: string
}

export type BrowserRow = {
  id: string
  entryDomainId: string
  createdAt: string
  mobileIsAlive: boolean | null
  desktopIsAlive: boolean | null
  r2Key: string | null
  rawBrowserMetadata: Record<string, any> | null
  descriptions?: BrowserDataDescription[]
}

export function findDescription(descriptions: BrowserDataDescription[] | undefined, tag: string) {
  return descriptions?.find((d) => d.tag === tag)?.description ?? null
}

export type ProbeResult = {
  id: string
  probeLayer: string
  testType: string
  isAlive: boolean | null
  rawResult: Record<string, any>
}

export type ProbeRun = {
  id: string
  entryDomainId: string
  createdAt: string
  probeIp: string | null
  probeNode: string | null
  triggeredBy: string | null
  results: ProbeResult[]
}

export type EntryDetailData = {
  userEntryId: string
  totalDomains: number
  domains: DomainDetail[]
  browserData: BrowserRow[]
  probeRuns: ProbeRun[]
}

// ─── Theme classes ────────────────────────────────────────────────────────────

export function watchClasses(theme: ProcessedTheme) {
  // Since phase 2 of the UI harmony: the design tokens (globals.css), the
  // same in both themes. `theme` is kept for the callers' signatures.
  void theme
  return {
    shell:  'bg-background text-foreground',
    card:   'border-border bg-card',
    panel:  'border-border bg-card',
    soft:   'bg-secondary',
    line:   'border-border',
    text:   'text-foreground',
    muted:  'text-muted-foreground',
    faint:  'text-subtle',
    label:  'text-muted-foreground',
    input:  'border-border bg-card text-foreground placeholder:text-subtle',
  }
}

// ─── Date utilities ───────────────────────────────────────────────────────────

export function compactDate(iso: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${d.getDate()} ${months[d.getMonth()]} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function compactDateNumeric(iso: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// Local time, like every other date formatter in this file (compactDate,
// compactDateNumeric) and fmtDate/fmtDateShort elsewhere — this one used to
// read UTC getters while everything else read local ones, so the exact same
// timestamp shown in the Campaign Status Timeline vs. anywhere else on the
// same page (BrowserDataPanel, ProbeRunsPanel, …) would disagree by
// whatever the browser's UTC offset happens to be (e.g. a full hour in
// Portugal's summer WEST).
export function timelineDateLabel(date: Date, includeYear = false) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const base = `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]}`
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  return includeYear ? `${base} ${date.getFullYear()} ${time}` : `${base} ${time}`
}

// ─── Post type helpers ────────────────────────────────────────────────────────

export const POST_TYPE_META: Record<string, { label: string; bg: string; text: string; border: string; activeCls: string }> = {
  call:    { label: 'Scall',   bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', activeCls: 'bg-purple-700 border-purple-700 text-white' },
  url:     { label: 'URL',     bg: 'bg-signal-surface',    text: 'text-signal',    border: 'border-signal/30',    activeCls: 'bg-primary text-primary-foreground border-signal/30 text-white' },
  text:    { label: 'Text',    bg: 'bg-warning-surface',  text: 'text-warning',  border: 'border-warning-border',  activeCls: 'bg-amber-600 border-amber-600 text-white' },
  image:   { label: 'Image',   bg: 'bg-teal-50',   text: 'text-teal-700',   border: 'border-teal-200',   activeCls: 'bg-teal-700 border-teal-700 text-white' },
  unknown: { label: 'Unknown', bg: 'bg-secondary',  text: 'text-muted-foreground',  border: 'border-border',  activeCls: 'bg-subtle border-border text-white' },
}

export function postTypeMeta(postType: string | null) {
  return POST_TYPE_META[postType ?? 'unknown'] ?? POST_TYPE_META.unknown
}

export function postTypeLabel(postType: string | null) { return postTypeMeta(postType).label }

export function postTypeStyle(postType: string | null) {
  const m = postTypeMeta(postType)
  return { badge: `${m.bg} ${m.text} ${m.border}`, active: m.activeCls }
}

// ─── Entry helpers ────────────────────────────────────────────────────────────

export function entryDisplayTitle(entry: ProcessedEntry) {
  return entry.headline ?? entry.title ?? entry.processedInputDescription?.slice(0, 90) ?? 'Untitled fraud narrative'
}

export function entryHostname(entry: ProcessedEntry) {
  if (!entry.originalUrl) return null
  try { return new URL(entry.originalUrl).hostname } catch { return entry.originalUrl }
}

export function campaignCode(entry: ProcessedEntry, domain?: DomainDetail | null) {
  const host = domain?.originalUrl
    ? (() => { try { return new URL(domain.originalUrl).hostname } catch { return domain.originalUrl } })()
    : entryHostname(entry)
  const base = (entry.headline ?? entry.title ?? host ?? 'campaign')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 4)
    .join('-')
    .toUpperCase()
  return base || 'CAMPAIGN'
}
